import {
  collection,
  doc,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocFromServer,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  deleteField
} from 'firebase/firestore';
import { db } from './config';
import { bracketArrayToMap, diffBracketMaps, SCHEMA_VERSION } from '../utils/bracketSchema';

// Generate a unique share URL
const generateShareUrl = () => {
  return Math.random().toString(36).substring(2, 10);
};

// Create a new tournament
export const createTournament = async (tournamentData, userId) => {
  try {
    // Check rate limit (max 5 tournaments per hour)
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    const recentTournamentsQuery = query(
      collection(db, 'tournaments'),
      where('createdBy', '==', userId)
    );

    const recentTournamentsSnapshot = await getDocs(recentTournamentsQuery);

    // Filter tournaments created in the last hour
    const tournamentsInLastHour = recentTournamentsSnapshot.docs.filter(doc => {
      const createdAt = doc.data().createdAt?.toDate?.();
      return createdAt && createdAt > oneHourAgo;
    });

    const MAX_TOURNAMENTS_PER_HOUR = 5;

    if (tournamentsInLastHour.length >= MAX_TOURNAMENTS_PER_HOUR) {
      return {
        id: null,
        tournament: null,
        error: new Error(`Rate limit exceeded. You can create a maximum of ${MAX_TOURNAMENTS_PER_HOUR} tournaments per hour. Please try again later.`)
      };
    }

    // Check total tournament limit (max 2 cloud-saved tournaments per free user)
    const userTournamentsQuery = query(
      collection(db, 'tournaments'),
      where('createdBy', '==', userId)
    );

    const userTournamentsSnapshot = await getDocs(userTournamentsQuery);
    const MAX_FREE_TOURNAMENTS = 2;

    if (userTournamentsSnapshot.size >= MAX_FREE_TOURNAMENTS) {
      return {
        id: null,
        tournament: null,
        error: new Error(`You've reached the maximum of ${MAX_FREE_TOURNAMENTS} cloud-saved tournaments on the free plan. Please delete an existing tournament to create a new one, or log out to run tournaments locally without saving to the cloud.`)
      };
    }

    const shareUrl = generateShareUrl();

    // Normalize the bracket payload to v2 schema. Callers may pass a legacy
    // `bracket` array (e.g. when promoting a local tournament to the cloud);
    // we convert it here so every new tournament document lands at SCHEMA_VERSION.
    const incomingBracket = Array.isArray(tournamentData.bracket) ? tournamentData.bracket : [];
    const { bracketMap, matchOrder } = bracketArrayToMap(incomingBracket);
    // Strip the legacy bracket key out of tournamentData so the spread below
    // doesn't reintroduce it.
    const { bracket: _legacyBracket, ...tournamentDataRest } = tournamentData;
    void _legacyBracket;

    const tournament = {
      ...tournamentDataRest,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      shareUrl,
      status: 'draft',
      isPublic: tournamentData.isPublic || false,
      title: tournamentData.title || 'Untitled Tournament',
      description: tournamentData.description || '',
      format: tournamentData.format || 'double_elimination',
      players: tournamentData.players || [],
      schemaVersion: SCHEMA_VERSION,
      bracketMap,
      matchOrder,
      payoutAmounts: tournamentData.payoutAmounts || [''],
      numPayoutPlaces: tournamentData.numPayoutPlaces || 3,
      tournamentSettings: {
        customTableNumbers: '1,2,3,4,5,6,7,8',
        ...tournamentData.tournamentSettings
      }
    };

    const docRef = await addDoc(collection(db, 'tournaments'), tournament);

    return {
      id: docRef.id,
      tournament: { ...tournament, id: docRef.id },
      error: null
    };
  } catch (error) {
    console.error('Error creating tournament:', error);
    return { id: null, tournament: null, error };
  }
};

/**
 * Recursively removes undefined values from an object.
 * Firebase Firestore does not accept undefined values.
 * @param {object} obj - The object to clean
 * @returns {object} - A new object with undefined values removed
 */
const removeUndefinedValues = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item));
  }

  if (typeof obj === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  }

  return obj;
};

// Update tournament
export const updateTournament = async (tournamentId, updates) => {
  try {
    const tournamentRef = doc(db, 'tournaments', tournamentId);

    // Clean undefined values before sending to Firebase
    const cleanedUpdates = removeUndefinedValues(updates);

    await updateDoc(tournamentRef, {
      ...cleanedUpdates,
      updatedAt: serverTimestamp()
    });

    return { error: null };
  } catch (error) {
    console.error('Error updating tournament:', error);
    return { error };
  }
};

/**
 * Deep-merge a nested update payload into a tournament document.
 *
 * Uses `setDoc(..., { merge: true })`, which:
 *  - Recursively merges maps (only leaf fields you include are changed, others
 *    under the same nested path are preserved).
 *  - Replaces arrays wholesale.
 *  - Honors `deleteField()` sentinels.
 *
 * Required for any write that targets a nested path whose dynamic segment
 * contains a `.` — most importantly the recurring-tournament instance date
 * keys (e.g. "2026-05-26T19:00:00.000"). The dot-notation `updateDoc` syntax
 * (`instances.<date>.bracketMap.<id>.<field>`) would split the date on every
 * dot and write to the wrong field path. setDoc-with-merge avoids that by
 * passing the structure as a real nested object.
 *
 * @param {string} tournamentId
 * @param {object} nestedUpdates - Nested object structure of changes to apply
 */
export const mergeTournament = async (tournamentId, nestedUpdates) => {
  try {
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const cleaned = removeUndefinedValues(nestedUpdates);
    await setDoc(tournamentRef, {
      ...cleaned,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { error: null };
  } catch (error) {
    console.error('Error merging tournament:', error);
    return { error };
  }
};

// Delete tournament
export const deleteTournament = async (tournamentId) => {
  try {
    await deleteDoc(doc(db, 'tournaments', tournamentId));
    return { error: null };
  } catch (error) {
    console.error('Error deleting tournament:', error);
    return { error };
  }
};

// Get tournament by ID
// Read one document, optionally forcing a fresh server round-trip. On resume
// from background the local cache can be stale, so callers recovering from a
// suspended realtime stream pass preferServer=true. Falls back to the normal
// cache-or-server read if the server is unreachable (offline).
const readDoc = async (ref, preferServer) => {
  if (preferServer) {
    try {
      return await getDocFromServer(ref);
    } catch (e) {
      // Offline / server unreachable — fall back to the standard read so we
      // still return whatever we have rather than throwing.
    }
  }
  return getDoc(ref);
};

export const getTournament = async (tournamentId, preferServer = false) => {
  try {
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const tournamentDoc = await readDoc(tournamentRef, preferServer);

    if (tournamentDoc.exists()) {
      return {
        tournament: { id: tournamentDoc.id, ...tournamentDoc.data() },
        error: null
      };
    } else {
      return {
        tournament: null,
        error: new Error('Tournament not found')
      };
    }
  } catch (error) {
    console.error('Error getting tournament:', error);
    return { tournament: null, error };
  }
};

/**
 * Replace a recurring-tournament instance's bracket with a reconstructed one.
 *
 * Used by the admin Instance Repair tool to fix instances corrupted by the old
 * stale-baseline sync bug. Performs a TWO-STEP clean replace because a single
 * setDoc(merge) would deep-merge the new bracketMap into the corrupt one, leaving
 * stale partial-match fields behind:
 *   1. deleteField() the existing bracketMap / matchOrder / legacy bracket.
 *   2. write the clean reconstructed bracketMap + matchOrder.
 *
 * The instance date key contains dots, so it is passed as a real nested object
 * (never a dotted updateDoc path). Only the bracket is touched — players, results
 * metadata, standings, payments, etc. are left untouched.
 *
 * @param {string} tournamentId
 * @param {string} instanceDate - Full instance key, e.g. "2026-06-02T19:30:00.000".
 * @param {object} bracketMap
 * @param {string[]} matchOrder
 */
export const repairInstanceBracket = async (tournamentId, instanceDate, bracketMap, matchOrder) => {
  try {
    const ref = instanceDocRef(tournamentId, instanceDate);
    // Step 1: clear the corrupt bracket data so the new map fully replaces it
    // (setDoc-merge would deep-merge the maps and leave stale match keys behind).
    await setDoc(ref, {
      bracketMap: deleteField(),
      matchOrder: deleteField(),
      bracket: deleteField(),
    }, { merge: true });
    // Step 2: write the clean reconstructed bracket.
    await setDoc(ref, {
      bracketMap,
      matchOrder,
      schemaRepairedAt: new Date().toISOString(),
    }, { merge: true });
    return { error: null };
  } catch (error) {
    console.error('Error repairing instance bracket:', error);
    return { error };
  }
};

// ---------------------------------------------------------------------------
// Recurring-tournament instances — subcollection tournaments/{id}/instances/{date}
//
// Each occurrence is its own document so the parent stays small and bounded no
// matter how many occurrences accumulate. Because the date is a doc ID (not a
// nested field key), bracket writes use ordinary `updateDoc` with dotted
// `bracketMap.<id>.<field>` paths — no more setDoc(merge) dot-key workaround.
// ---------------------------------------------------------------------------

const instanceDocRef = (tournamentId, date) =>
  doc(db, 'tournaments', tournamentId, 'instances', date);

// One-time fetch of a single occurrence.
export const getInstance = async (tournamentId, date, preferServer = false) => {
  try {
    const snap = await readDoc(instanceDocRef(tournamentId, date), preferServer);
    return { instance: snap.exists() ? { id: snap.id, ...snap.data() } : null, error: null };
  } catch (error) {
    console.error('Error getting instance:', error);
    return { instance: null, error };
  }
};

// Real-time subscription to one occurrence document.
// `includeMetadataChanges` so the callback also fires on write-ack transitions:
// `metadata.hasPendingWrites` tells the director whether their edits have
// actually reached the server (vs. sitting in the offline cache behind a green
// LED). The metadata object is passed as the callback's second argument.
export const subscribeToInstance = (tournamentId, date, callback, onError) => {
  return onSnapshot(
    instanceDocRef(tournamentId, date),
    { includeMetadataChanges: true },
    (snap) => {
      const meta = {
        hasPendingWrites: snap.metadata.hasPendingWrites,
        fromCache: snap.metadata.fromCache,
      };
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null, meta);
    },
    (error) => {
      console.error('Error in instance subscription:', error);
      if (onError) onError(error); else callback(null);
    }
  );
};

// Full write of an occurrence document — used on start / reset, and as the
// create path for a brand-new occurrence.
export const setInstance = async (tournamentId, date, fullData) => {
  try {
    const cleaned = removeUndefinedValues(fullData);
    await setDoc(instanceDocRef(tournamentId, date), {
      ...cleaned,
      lastUpdated: new Date().toISOString(),
    });
    return { error: null };
  } catch (error) {
    console.error('Error setting instance:', error);
    return { error };
  }
};

/**
 * Incremental occurrence write: per-match bracketMap field diffs + changed
 * metadata leaves. Only the fields that changed are sent (bandwidth-friendly on
 * poor connections). If the occurrence document does not exist yet (`docExists`
 * false), writes the full document instead so a first save can't land partial
 * match objects.
 *
 * @param {object} args
 * @param {object} args.prevBracketMap - last-synced map for this occurrence (for diffing)
 * @param {object} args.nextBracketMap - full current map
 * @param {string[]} [args.matchOrder] - full current order (written if changed/new)
 * @param {object} [args.metadata] - flat instance-level fields to update (players, completed, …)
 * @param {boolean} args.docExists - whether the occurrence doc already exists
 */
export const writeInstanceBracketDiff = async (
  tournamentId,
  date,
  { prevBracketMap, nextBracketMap, matchOrder, metadata, docExists }
) => {
  try {
    const ref = instanceDocRef(tournamentId, date);
    const meta = metadata ? removeUndefinedValues(metadata) : {};

    if (!docExists) {
      // Create path — write the full occurrence document.
      await setDoc(ref, {
        ...meta,
        bracketMap: nextBracketMap || {},
        matchOrder: Array.isArray(matchOrder) ? matchOrder : [],
        lastUpdated: new Date().toISOString(),
      });
      return { error: null };
    }

    const updates = { ...meta };
    Object.assign(updates, diffBracketMaps(prevBracketMap || {}, nextBracketMap || {}, 'bracketMap'));
    if (Array.isArray(matchOrder)) updates.matchOrder = matchOrder;
    if (Object.keys(updates).length === 0) return { error: null };
    updates.lastUpdated = new Date().toISOString();
    await updateDoc(ref, removeUndefinedValues(updates));
    return { error: null };
  } catch (error) {
    console.error('Error writing instance bracket diff:', error);
    return { error };
  }
};

// Merge a tiny per-occurrence summary into the parent's `instanceIndex` map so
// the navigator can list occurrences without reading any instance documents.
// Nested object (not a dotted path) keeps the dotted date key intact.
export const updateInstanceIndex = async (tournamentId, date, summary) => {
  try {
    await setDoc(doc(db, 'tournaments', tournamentId), {
      instanceIndex: { [date]: removeUndefinedValues(summary) },
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return { error: null };
  } catch (error) {
    console.error('Error updating instance index:', error);
    return { error };
  }
};

// Get tournament by share URL
export const getTournamentByShareUrl = async (shareUrl) => {
  try {
    const q = query(
      collection(db, 'tournaments'),
      where('shareUrl', '==', shareUrl),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        tournament: { id: doc.id, ...doc.data() },
        error: null
      };
    } else {
      return {
        tournament: null,
        error: new Error('Tournament not found')
      };
    }
  } catch (error) {
    console.error('Error getting tournament by share URL:', error);
    return { tournament: null, error };
  }
};

// Get user's tournaments
export const getUserTournaments = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'tournaments'),
      where('createdBy', '==', userId),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const tournaments = [];

    querySnapshot.forEach((doc) => {
      tournaments.push({ id: doc.id, ...doc.data() });
    });

    // Sort by updatedAt in JavaScript since we removed the orderBy from the query
    tournaments.sort((a, b) => {
      const aTime = a.updatedAt?.toDate?.() || new Date(0);
      const bTime = b.updatedAt?.toDate?.() || new Date(0);
      return bTime - aTime; // Descending order
    });

    return { tournaments, error: null };
  } catch (error) {
    console.error('Error getting user tournaments:', error);
    return { tournaments: [], error };
  }
};

// Get public tournaments
export const getPublicTournaments = async (limitCount = 20) => {
  try {
    const q = query(
      collection(db, 'tournaments'),
      where('isPublic', '==', true),
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const tournaments = [];

    querySnapshot.forEach((doc) => {
      tournaments.push({ id: doc.id, ...doc.data() });
    });

    return { tournaments, error: null };
  } catch (error) {
    console.error('Error getting public tournaments:', error);
    return { tournaments: [], error };
  }
};

// Subscribe to tournament updates (real-time)
export const subscribeToTournament = (tournamentId, callback, onError) => {
  const tournamentRef = doc(db, 'tournaments', tournamentId);

  return onSnapshot(tournamentRef, (doc) => {
    if (doc.exists()) {
      callback({ id: doc.id, ...doc.data() });
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error in tournament subscription:', error);
    // Call the error handler if provided, otherwise just callback with null
    if (onError) {
      onError(error);
    } else {
      callback(null);
    }
  });
};

// Subscribe to user tournaments (real-time)
export const subscribeToUserTournaments = (userId, callback, limitCount = 50) => {
  const q = query(
    collection(db, 'tournaments'),
    where('createdBy', '==', userId),
    limit(limitCount)
  );

  return onSnapshot(q, (querySnapshot) => {
    const tournaments = [];
    querySnapshot.forEach((doc) => {
      tournaments.push({ id: doc.id, ...doc.data() });
    });

    // Sort by updatedAt in JavaScript since we removed the orderBy from the query
    tournaments.sort((a, b) => {
      const aTime = a.updatedAt?.toDate?.() || new Date(0);
      const bTime = b.updatedAt?.toDate?.() || new Date(0);
      return bTime - aTime; // Descending order
    });

    callback(tournaments);
  }, (error) => {
    console.error('Error in user tournaments subscription:', error);
    callback([]);
  });
};

// Save tournament from local data (migration helper)
export const saveTournamentFromLocal = async (localTournamentData, userId) => {
  try {
    // Convert local tournament data to Firebase format
    const tournamentData = {
      title: `Tournament ${new Date().toLocaleDateString()}`,
      description: `Imported tournament with ${localTournamentData.players?.length || 0} players`,
      format: localTournamentData.tournamentFormat || 'double_elimination',
      players: localTournamentData.playersText?.split('\n').filter(Boolean) || [],
      bracket: localTournamentData.bracket || [],
      payoutAmounts: localTournamentData.payoutAmounts || [''],
      numPayoutPlaces: localTournamentData.numPayoutPlaces || 3,
      tournamentSettings: localTournamentData.tournamentSettings || {},
      status: localTournamentData.bracket?.length > 0 ? 'active' : 'draft'
      // Note: isComplete field removed - use status === 'completed' instead
    };

    return await createTournament(tournamentData, userId);
  } catch (error) {
    console.error('Error saving tournament from local data:', error);
    return { id: null, tournament: null, error };
  }
};

// Duplicate tournament
export const duplicateTournament = async (tournamentId, userId) => {
  try {
    const { tournament, error } = await getTournament(tournamentId);

    if (error || !tournament) {
      return { id: null, tournament: null, error: error || new Error('Tournament not found') };
    }

    // Create a copy with reset data. The bracket starts empty — createTournament
    // will produce an empty bracketMap/matchOrder at the current SCHEMA_VERSION.
    const duplicatedData = {
      title: `${tournament.title} (Copy)`,
      description: tournament.description,
      format: tournament.format,
      players: tournament.players || [],
      payoutAmounts: tournament.payoutAmounts || [''],
      numPayoutPlaces: tournament.numPayoutPlaces || 3,
      tournamentSettings: tournament.tournamentSettings || {},
      status: 'draft',
      // Note: isComplete field removed - use status === 'completed' instead
      isPublic: false
    };

    return await createTournament(duplicatedData, userId);
  } catch (error) {
    console.error('Error duplicating tournament:', error);
    return { id: null, tournament: null, error };
  }
};

/**
 * One-time migration: Rename startTime/endTime to actualStartTime/actualEndTime
 * Run from browser console: migrateMatchTimingFields()
 * This is exposed on window for easy access
 */
export const migrateMatchTimingFields = async () => {
  console.log('Starting match timing field migration...\n');

  let totalMatchesUpdated = 0;
  let tournamentsProcessed = 0;

  try {
    const tournamentsSnapshot = await getDocs(collection(db, 'tournaments'));
    console.log(`Found ${tournamentsSnapshot.size} tournaments to check\n`);

    for (const tournamentDoc of tournamentsSnapshot.docs) {
      const tournament = tournamentDoc.data();
      const tournamentId = tournamentDoc.id;
      let tournamentUpdated = false;
      const updates = {};

      // Check main bracket
      if (tournament.bracket && Array.isArray(tournament.bracket)) {
        let bracketMatchCount = 0;
        const updatedBracket = tournament.bracket.map((match, index) => {
          const updatedMatch = { ...match };
          let matchUpdated = false;

          if (match.startTime !== undefined && match.actualStartTime === undefined) {
            updatedMatch.actualStartTime = match.startTime;
            delete updatedMatch.startTime;
            matchUpdated = true;
          }

          if (match.endTime !== undefined && match.actualEndTime === undefined) {
            updatedMatch.actualEndTime = match.endTime;
            delete updatedMatch.endTime;
            matchUpdated = true;
          }

          if (matchUpdated) {
            console.log(`  Tournament ${tournamentId}: Match ${index + 1} (bracket) - migrated`);
            bracketMatchCount++;
          }

          return updatedMatch;
        });

        if (bracketMatchCount > 0) {
          totalMatchesUpdated += bracketMatchCount;
          tournamentUpdated = true;
          updates.bracket = updatedBracket;
        }
      }

      // Check instances (for recurring tournaments)
      if (tournament.instances && typeof tournament.instances === 'object') {
        const updatedInstances = { ...tournament.instances };
        let instancesUpdated = false;
        let instanceMatchCount = 0;

        for (const [instanceDate, instanceData] of Object.entries(tournament.instances)) {
          if (instanceData && instanceData.bracket && Array.isArray(instanceData.bracket)) {
            let thisInstanceMatchCount = 0;
            const updatedInstanceBracket = instanceData.bracket.map((match, index) => {
              const updatedMatch = { ...match };
              let matchUpdated = false;

              if (match.startTime !== undefined && match.actualStartTime === undefined) {
                updatedMatch.actualStartTime = match.startTime;
                delete updatedMatch.startTime;
                matchUpdated = true;
              }

              if (match.endTime !== undefined && match.actualEndTime === undefined) {
                updatedMatch.actualEndTime = match.endTime;
                delete updatedMatch.endTime;
                matchUpdated = true;
              }

              if (matchUpdated) {
                console.log(`  Tournament ${tournamentId}: Instance ${instanceDate}, Match ${index + 1} - migrated`);
                thisInstanceMatchCount++;
              }

              return updatedMatch;
            });

            if (thisInstanceMatchCount > 0) {
              instancesUpdated = true;
              instanceMatchCount += thisInstanceMatchCount;
              updatedInstances[instanceDate] = {
                ...instanceData,
                bracket: updatedInstanceBracket
              };
            }
          }
        }

        if (instancesUpdated) {
          updates.instances = updatedInstances;
          totalMatchesUpdated += instanceMatchCount;
          tournamentUpdated = true;
        }
      }

      // Apply updates
      if (tournamentUpdated) {
        await updateDoc(doc(db, 'tournaments', tournamentId), updates);
        tournamentsProcessed++;
        console.log(`  ✓ Tournament ${tournamentId} updated\n`);
      }
    }

    console.log('\n========================================');
    console.log('Migration complete!');
    console.log(`Tournaments updated: ${tournamentsProcessed}`);
    console.log(`Total matches migrated: ${totalMatchesUpdated}`);
    console.log('========================================\n');

    return { success: true, tournamentsProcessed, totalMatchesUpdated };
  } catch (error) {
    console.error('Migration failed:', error);
    return { success: false, error: error.message };
  }
};

// Expose migration function on window for easy console access
if (typeof window !== 'undefined') {
  window.migrateMatchTimingFields = migrateMatchTimingFields;
}